import {
  HStack,
  IconButton,
  Tooltip,
  Button,
  VStack,
  Center,
  Box,
  Text,
  useBreakpointValue,
  Switch,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FaPlay,
  FaPause,
  FaStepForward,
  FaStepBackward,
  FaFastForward,
  FaFastBackward,
  FaEyeSlash,
  FaMapMarker,
  FaMapMarkerAlt,
  FaArrowRight,
  FaArrowDown,
  FaVolumeDown,
  FaSync,
} from 'react-icons/fa';
import { TbRepeat, TbArrowBarToDown } from 'react-icons/tb';
import { BsArrowRightSquare } from 'react-icons/bs';
import { useAudioContext } from '../context/AudioContext';
import { useEffect, useState, useRef, useCallback } from 'react';
import { setupKeyboardHandlers } from '../utils/playerSwitching';
import { useRandomize } from '../utils/randomize';
import ShareButton from './ShareButton';
import DownloadButton from './DownloadButton';
import AddFilesButton from './AddFilesButton';
import ReloadButton from './ReloadButton';
import FilterControlButton from './FilterControlButton';
import { LoadedFile } from '../services/FileService';
import { useSync } from '../features/sync/context/SyncContext';
import { naturalSortNumbers } from '../utils/sorting';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { 
    color: #E53E3E; /* red.600 - deeper red */
    transform: scale(1);
  }
  50% { 
    color: #DD6B20; /* orange.600 - matches accent color */
    transform: scale(1.15);
  }
  100% { 
    color: #E53E3E; /* red.600 */
    transform: scale(1);
  }
`;

const pulseSync = keyframes`
  0% { 
    transform: scale(1);
  }
  50% { 
    transform: scale(1.15);
  }
  100% { 
    transform: scale(1);
  }
`;

const pulseRed = keyframes`
  0% { 
    color: #E53E3E; /* red.600 */
  }
  25% { 
    color: #E53E3E; /* red.600 - stay red */
  }
  50% { 
    color: #ffa726; /* accent.400 - orange highlight */
  }
  75% { 
    color: #ffa726; /* accent.400 - stay orange */
  }
  100% { 
    color: #E53E3E; /* red.600 */
  }
`;

const TooltipKeyBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    px={2}
    py={0.5}
    bg="gray.700"
    borderRadius="md"
    fontSize="sm"
    border="1px solid"
    borderColor="gray.500"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    minW="24px"
    h="24px"
    color="gray.100"
    fontWeight="medium"
    _hover={{
      bg: "gray.600",
    }}
  >
    {children}
  </Box>
);

interface TransportControlsProps {
  onPlayPause: () => void;
  onForward: (seconds?: number) => void;
  onRewind: (seconds?: number) => void;
  onRewindToLastStart: () => void;
  isPlaying: boolean;
  onLoop?: () => void;
  onBlindTest?: () => void;
  playerCount: number;
  shareData?: {
    isOwner: boolean;
    allowDownloads: boolean;
    originalUrls: string[];
  } | null;
  onRemove?: (playerNumber: number) => void;
  onFilesSelected?: (files: File[], urls: string[], shareableUrls: string[]) => void;
  loadedFiles?: LoadedFile[];
  onAddFilesLoadingChange?: (isLoading: boolean) => void;
  onAddFilesProgressChange?: (downloadedBytes: number, totalBytes: number) => void;
  onFilterModalOpen?: () => void;
  onToggleFilter?: () => void;
  onCreateRegion?: () => void;
  hasFilterBeenSet?: boolean;
}

// Stereo Mode Icon Component
const StereoModeIcon = ({ mode, size = 24 }: { mode: 'stereo' | 'mono' | 'side'; size?: number }) => {
  const iconSize = size;
  const strokeWidth = 2;
  
  if (mode === 'stereo') {
    // Two overlapping circles for stereo
    const viewBoxWidth = 32;
    const viewBoxHeight = 24;
    const scaleFactor = viewBoxWidth / viewBoxHeight;
    return (
      <svg
        width={iconSize * scaleFactor}
        height={iconSize}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Left circle */}
        <circle
          cx={viewBoxWidth * 0.3}
          cy={viewBoxHeight / 2}
          r={viewBoxHeight * 0.35}
          stroke="currentColor"
          strokeWidth={strokeWidth}
        />
        {/* Right circle */}
        <circle
          cx={viewBoxWidth * 0.7}
          cy={viewBoxHeight / 2}
          r={viewBoxHeight * 0.35}
          stroke="currentColor"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  } else if (mode === 'mono') {
    // Single circle for mono
    return (
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="8"
          stroke="currentColor"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  } else if (mode === 'side') {
    // Side icon (two circles with gap/space between)
    const viewBoxWidth = 32;
    const viewBoxHeight = 24;
    const scaleFactor = viewBoxWidth / viewBoxHeight;
    return (
      <svg
        width={iconSize * scaleFactor}
        height={iconSize}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Left circle (hollow) */}
        <circle
          cx="8"
          cy="12"
          r="6"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Right circle (hollow) */}
        <circle
          cx="24"
          cy="12"
          r="6"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
        />
      </svg>
    );
  }
};

const TransportControls: React.FC<TransportControlsProps> = ({
  onPlayPause,
  onForward,
  onRewind,
  onRewindToLastStart,
  isPlaying,
  onLoop,
  onBlindTest,
  playerCount,
  shareData,
  onRemove,
  onFilesSelected,
  loadedFiles = [],
  onAddFilesLoadingChange,
  onAddFilesProgressChange,
  onFilterModalOpen,
  onToggleFilter,
  hasFilterBeenSet = false,
  onCreateRegion,
}) => {
  const { 
    selectedPlayer, 
    selectPlayer, 
    audioElements, 
    isBlindTestMode, 
    blindTestMode,
    toggleBlindTestMode,
    isLooping,
    loopState,
    setLoopStart,
    setLoopEnd,
    disableLoop,
    isRandomSwitching,
    toggleRandomSwitching,
    blindTestIncludedPlayers,
    toggleBlindTestIncludedForSelected,
    toggleAllBlindTestIncluded,
    lastStartPositions,
    setLastStartPositions,
    toggleStereoMode,
    filterType,
    filterFrequency,
    stereoMode,
    isDimmed,
    toggleMasterDim,
    isSpinning,
    triggerSpinAnimation,
    blindTestAllowRepeats,
    pauseAll,
  } = useAudioContext();

  const {
    syncPoints,
    hasSyncPoints,
    setSyncPoint,
    toggleSyncPoint,
    clearSyncPoints,
    jumpToSyncPoints,
    hasJumpedToSyncPoints,
  } = useSync();

  const { synchronizeWaveforms } = useAudioContext();

  const { getRandomPlayer } = useRandomize(playerCount, isBlindTestMode ? Array.from(audioElements.keys())
    .filter(playerNum => blindTestIncludedPlayers.get(playerNum) ?? true)
    .sort(naturalSortNumbers) : undefined);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const hasLongPressed = useRef(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [showSwitchTooltip, setShowSwitchTooltip] = useState(false);
  const hasShownTooltip = useRef(false);
  const [isSynchronizing, setIsSynchronizing] = useState(false);

  // Show tooltip when basic blind mode is first activated
  useEffect(() => {
    if (isBlindTestMode && blindTestMode === 'basic' && !hasShownTooltip.current && isMobile) {
      setShowSwitchTooltip(true);
      hasShownTooltip.current = true;
    }
  }, [isBlindTestMode, blindTestMode]);

  // Hide tooltip when clicking anywhere
  useEffect(() => {
    const handleClick = () => {
      setShowSwitchTooltip(false);
    };

    if (showSwitchTooltip) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showSwitchTooltip]);

  const handleLoopClick = () => {
    const audio = audioElements.get(selectedPlayer);
    if (!audio) return;

    // Allow loop clearing even when paused
    if (isLooping) {
      disableLoop();
      return;
    }

    // Only allow setting loop points when playing
    if (!isPlaying) return;

    if (loopState === 'inactive') {
      setLoopStart(audio.currentTime, syncPoints, hasJumpedToSyncPoints);
    } else if (loopState === 'setting_end') {
      setLoopEnd(audio.currentTime, syncPoints, hasJumpedToSyncPoints);
    }
  };

  // Add effect to reset loop state when playback stops
  useEffect(() => {
    if (!isPlaying && loopState === 'setting_end') {
      disableLoop();
    }
  }, [isPlaying, loopState, disableLoop]);

  const handleSyncPoint = () => {
    const audio = audioElements.get(selectedPlayer);
    if (audio) {
      const currentTime = audio.currentTime;
      // Ensure currentTime is a valid number (including 0)
      if (typeof currentTime === 'number' && !isNaN(currentTime) && isFinite(currentTime)) {
        toggleSyncPoint(selectedPlayer, Math.max(0, currentTime));
      }
    }
  };

  const handleJumpToSyncPoints = () => {
    if (hasJumpedToSyncPoints) {
      clearSyncPoints();
      audioElements.forEach(audio => {
        audio.currentTime = 0;
      });
    } else if (hasSyncPoints) {
      jumpToSyncPoints();
    }
  };

  // Handle waveform synchronization (triggered by holding S key)
  const handleWaveformSync = useCallback(async () => {
    if (audioElements.size < 2) {
      console.warn('[TransportControls] Need at least 2 players to synchronize');
      return;
    }

    if (isSynchronizing) {
      return; // Prevent multiple simultaneous synchronizations
    }

    setIsSynchronizing(true);
    console.log('[TransportControls] Starting waveform synchronization...');
    
    try {
      // Use selected player as reference
      const syncOffsets = await synchronizeWaveforms(selectedPlayer);
      
      if (syncOffsets && syncOffsets.size > 0) {
        // Set sync points based on synchronization offsets
        // The offset represents how much later each track starts relative to the reference
        // We'll set sync points at the current time for each player, adjusted by their offset
        const referenceAudio = audioElements.get(selectedPlayer);
        if (referenceAudio) {
          const referenceTime = referenceAudio.currentTime;
          
          // Clear existing sync points
          clearSyncPoints();
          
          // Calculate sync points and store them in a map for immediate use
          const calculatedSyncPoints = new Map<number, number>();
          
          // Set sync points based on offset interpretation:
          // Positive offset means comparison's time 0 matches reference's time +offset
          // So to sync: reference should be at time +offset when comparison is at time 0
          syncOffsets.forEach((offset, playerNumber) => {
            if (playerNumber !== selectedPlayer) {
              const playerAudio = audioElements.get(playerNumber);
              const referenceAudio = audioElements.get(selectedPlayer);
              if (playerAudio && referenceAudio) {
                const referenceDuration = referenceAudio.duration || 0;
                
                if (offset > 0) {
                  // Positive offset: comparison's time 0 matches reference's time +offset
                  // So comparison starts later - seek reference forward by offset to align
                  // Set reference sync point at referenceTime + offset, comparison sync point at referenceTime
                  const referenceSyncTime = Math.min(referenceTime + offset, referenceDuration);
                  calculatedSyncPoints.set(selectedPlayer, referenceSyncTime);
                  calculatedSyncPoints.set(playerNumber, referenceTime);
                  setSyncPoint(selectedPlayer, referenceSyncTime);
                  setSyncPoint(playerNumber, referenceTime);
                  console.log(`[TransportControls] Set sync points (pos offset): reference at ${referenceSyncTime.toFixed(3)}s, player ${playerNumber} at ${referenceTime.toFixed(3)}s (offset: ${offset.toFixed(3)}s)`);
                } else {
                  // Negative offset: comparison's time 0 matches reference's time -|offset| (earlier)
                  // So comparison starts earlier - seek comparison forward by |offset| to align
                  // Set reference sync point at referenceTime, comparison sync point at referenceTime - offset
                  const playerDuration = playerAudio.duration || 0;
                  const comparisonSyncTime = Math.min(referenceTime - offset, playerDuration); // offset is negative, so -offset is positive
                  calculatedSyncPoints.set(selectedPlayer, referenceTime);
                  calculatedSyncPoints.set(playerNumber, comparisonSyncTime);
                  setSyncPoint(selectedPlayer, referenceTime);
                  setSyncPoint(playerNumber, comparisonSyncTime);
                  console.log(`[TransportControls] Set sync points (neg offset): reference at ${referenceTime.toFixed(3)}s, player ${playerNumber} at ${comparisonSyncTime.toFixed(3)}s (offset: ${offset.toFixed(3)}s)`);
                }
              }
            } else {
              // Reference player - only set if offset is negative (will be overwritten if positive)
              if (offset <= 0) {
                calculatedSyncPoints.set(selectedPlayer, referenceTime);
                setSyncPoint(selectedPlayer, referenceTime);
              }
            }
          });
          
          // Directly offset the playheads to the sync points (like manual sync does)
          // This ensures the playheads are immediately offset, not waiting for state updates
          audioElements.forEach((audio, playerNumber) => {
            const syncPoint = calculatedSyncPoints.get(playerNumber);
            if (syncPoint !== undefined) {
              audio.currentTime = Math.max(0, Math.min(syncPoint, audio.duration || Infinity));
              console.log(`[TransportControls] Offset playhead for player ${playerNumber} to ${syncPoint.toFixed(3)}s`);
            }
          });
          
          // Also call jumpToSyncPoints to update the state flag
          jumpToSyncPoints();
          
          console.log('[TransportControls] Synchronization complete, sync points set, and playheads offset');
        }
      } else {
        console.warn('[TransportControls] Synchronization failed or returned no results');
      }
    } finally {
      setIsSynchronizing(false);
    }
  }, [audioElements, selectedPlayer, synchronizeWaveforms, setSyncPoint, clearSyncPoints, jumpToSyncPoints, isSynchronizing]);

  const handleClearSyncPoints = () => {
    clearSyncPoints();
    audioElements.forEach(audio => {
      audio.currentTime = 0;
    });
  };

  // Combined sync handler: autosync if no anchors, manual sync if anchors exist
  const handleSyncButton = useCallback(() => {
    if (hasSyncPoints) {
      // Manual sync: jump to sync points
      handleJumpToSyncPoints();
    } else {
      // Autosync: analyze waveforms
      handleWaveformSync();
    }
  }, [hasSyncPoints, handleJumpToSyncPoints, handleWaveformSync]);

  // Helper function to update last start positions when seeking during playback
  const updateLastStartPositions = () => {
    if (isPlaying) {
      const newStartPositions = new Map<number, number>();
      audioElements.forEach((audio, playerNumber) => {
        newStartPositions.set(playerNumber, audio.currentTime);
      });
      setLastStartPositions(newStartPositions);
    }
  };

  // Maintain sync when anchors are active while preserving mobile smoothness
  const seekForward = (seconds: number = 3) => {
    if (hasJumpedToSyncPoints && syncPoints.size > 1) {
      const reference = audioElements.get(selectedPlayer);
      if (!reference) {
        console.log('[Sync Forward] No reference audio element found');
        return;
      }
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        // Capture reference time once at the start
        const referenceCurrentTime = reference.currentTime;
        // Calculate target time for reference player
        const referenceTargetTime = Math.min(referenceCurrentTime + seconds, reference.duration || Infinity);
        // Calculate the new offset from sync point
        const targetOffset = referenceTargetTime - referenceSyncPoint;
        const absoluteTargetForUnsynced = referenceTargetTime;

        console.log('[Sync Forward] Starting forward', {
          seconds,
          selectedPlayer,
          referenceCurrentTime: referenceCurrentTime.toFixed(3),
          referenceTargetTime: referenceTargetTime.toFixed(3),
          referenceSyncPoint: referenceSyncPoint.toFixed(3),
          targetOffset: targetOffset.toFixed(3),
          absoluteTargetForUnsynced: absoluteTargetForUnsynced.toFixed(3),
          syncPoints: Array.from(syncPoints.entries()).map(([p, t]) => ({ player: p, syncPoint: t.toFixed(3) })),
        });

        const beforeTimes = new Map<number, number>();
        audioElements.forEach((audio, playerNumber) => {
          beforeTimes.set(playerNumber, audio.currentTime);
        });

        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const t = Math.min(playerSyncPoint + targetOffset, audio.duration || Infinity);
            audio.currentTime = Math.max(0, t);
            console.log('[Sync Forward] Player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: t.toFixed(3),
              playerSyncPoint: playerSyncPoint.toFixed(3),
              calculatedTime: (playerSyncPoint + targetOffset).toFixed(3),
              duration: audio.duration?.toFixed(3),
            });
          } else {
            audio.currentTime = absoluteTargetForUnsynced;
            console.log('[Sync Forward] Unsynced player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: absoluteTargetForUnsynced.toFixed(3),
            });
          }
        });

        if (isMobile) {
          requestAnimationFrame(() => {
            // Use the same calculated values to ensure consistency
            console.log('[Sync Forward] Mobile RAF callback - applying same values', {
              targetOffset: targetOffset.toFixed(3),
              absoluteTargetForUnsynced: absoluteTargetForUnsynced.toFixed(3),
            });

            audioElements.forEach((audio, playerNumber) => {
              const playerSyncPoint = syncPoints.get(playerNumber);
              if (playerSyncPoint !== undefined) {
                const t = Math.min(playerSyncPoint + targetOffset, audio.duration || Infinity);
                audio.currentTime = Math.max(0, t);
                console.log('[Sync Forward] Mobile RAF - Player', playerNumber, {
                  beforeRAF: beforeTimes.get(playerNumber)?.toFixed(3),
                  afterRAF: t.toFixed(3),
                  playerSyncPoint: playerSyncPoint.toFixed(3),
                  calculatedTime: (playerSyncPoint + targetOffset).toFixed(3),
                });
              } else {
                audio.currentTime = absoluteTargetForUnsynced;
                console.log('[Sync Forward] Mobile RAF - Unsynced player', playerNumber, {
                  beforeRAF: beforeTimes.get(playerNumber)?.toFixed(3),
                  afterRAF: absoluteTargetForUnsynced.toFixed(3),
                });
              }
            });
          });
        }
        // Update last start positions if playing
        updateLastStartPositions();
        return;
      } else {
        console.log('[Sync Forward] Reference sync point not found for selected player', selectedPlayer);
      }
    }
    onForward(seconds);
  };

  const seekRewind = (seconds: number = 3) => {
    if (hasJumpedToSyncPoints && syncPoints.size > 1) {
      const reference = audioElements.get(selectedPlayer);
      if (!reference) {
        console.log('[Sync Rewind] No reference audio element found');
        return;
      }
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        // Capture reference time once at the start
        const referenceCurrentTime = reference.currentTime;
        // Calculate target time for reference player
        const referenceTargetTime = Math.max(0, referenceCurrentTime - seconds);
        // Calculate the new offset from sync point
        const targetOffset = referenceTargetTime - referenceSyncPoint;
        const absoluteTargetForUnsynced = referenceTargetTime;

        console.log('[Sync Rewind] Starting rewind', {
          seconds,
          selectedPlayer,
          referenceCurrentTime: referenceCurrentTime.toFixed(3),
          referenceTargetTime: referenceTargetTime.toFixed(3),
          referenceSyncPoint: referenceSyncPoint.toFixed(3),
          targetOffset: targetOffset.toFixed(3),
          absoluteTargetForUnsynced: absoluteTargetForUnsynced.toFixed(3),
          syncPoints: Array.from(syncPoints.entries()).map(([p, t]) => ({ player: p, syncPoint: t.toFixed(3) })),
        });

        const beforeTimes = new Map<number, number>();
        audioElements.forEach((audio, playerNumber) => {
          beforeTimes.set(playerNumber, audio.currentTime);
        });

        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const t = Math.max(0, Math.min(playerSyncPoint + targetOffset, audio.duration || Infinity));
            audio.currentTime = t;
            console.log('[Sync Rewind] Player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: t.toFixed(3),
              playerSyncPoint: playerSyncPoint.toFixed(3),
              calculatedTime: (playerSyncPoint + targetOffset).toFixed(3),
              duration: audio.duration?.toFixed(3),
            });
          } else {
            audio.currentTime = absoluteTargetForUnsynced;
            console.log('[Sync Rewind] Unsynced player', playerNumber, {
              before: beforeTimes.get(playerNumber)?.toFixed(3),
              after: absoluteTargetForUnsynced.toFixed(3),
            });
          }
        });

        if (isMobile) {
          requestAnimationFrame(() => {
            // Use the same calculated values to ensure consistency
            console.log('[Sync Rewind] Mobile RAF callback - applying same values', {
              targetOffset: targetOffset.toFixed(3),
              absoluteTargetForUnsynced: absoluteTargetForUnsynced.toFixed(3),
            });

            audioElements.forEach((audio, playerNumber) => {
              const playerSyncPoint = syncPoints.get(playerNumber);
              if (playerSyncPoint !== undefined) {
                const t = Math.max(0, Math.min(playerSyncPoint + targetOffset, audio.duration || Infinity));
                audio.currentTime = t;
                console.log('[Sync Rewind] Mobile RAF - Player', playerNumber, {
                  beforeRAF: beforeTimes.get(playerNumber)?.toFixed(3),
                  afterRAF: t.toFixed(3),
                  playerSyncPoint: playerSyncPoint.toFixed(3),
                  calculatedTime: (playerSyncPoint + targetOffset).toFixed(3),
                });
              } else {
                audio.currentTime = absoluteTargetForUnsynced;
                console.log('[Sync Rewind] Mobile RAF - Unsynced player', playerNumber, {
                  beforeRAF: beforeTimes.get(playerNumber)?.toFixed(3),
                  afterRAF: absoluteTargetForUnsynced.toFixed(3),
                });
              }
            });
          });
        }
        // Update last start positions if playing
        updateLastStartPositions();
        return;
      } else {
        console.log('[Sync Rewind] Reference sync point not found for selected player', selectedPlayer);
      }
    }
    onRewind(seconds);
  };

  const rewindToLastStartSyncAware = () => {
    if (hasJumpedToSyncPoints && syncPoints.size > 1) {
      const reference = audioElements.get(selectedPlayer);
      if (!reference) return;
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        // Get the last start position for the reference player
        const referenceStartPosition = lastStartPositions.get(selectedPlayer);
        if (referenceStartPosition === undefined) {
          console.warn('No last start position found for selected player');
          return;
        }
        
        // Calculate the offset from the sync point to the start position
        const startOffset = referenceStartPosition - referenceSyncPoint;
        
        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const playerStartPosition = lastStartPositions.get(playerNumber);
            if (playerStartPosition !== undefined) {
              // Use the stored start position for this player
              const t = Math.max(0, Math.min(playerStartPosition, audio.duration || Infinity));
              audio.currentTime = t;
            } else {
              // Fallback: calculate based on reference offset
              const t = Math.max(0, Math.min(playerSyncPoint + startOffset, audio.duration || Infinity));
              audio.currentTime = t;
            }
          } else {
            // For unsynced players, use their stored start position or fallback to reference
            const playerStartPosition = lastStartPositions.get(playerNumber);
            if (playerStartPosition !== undefined) {
              const t = Math.max(0, Math.min(playerStartPosition, audio.duration || Infinity));
              audio.currentTime = t;
            } else {
              audio.currentTime = referenceStartPosition;
            }
          }
        });

        if (isMobile) {
          requestAnimationFrame(() => {
            audioElements.forEach((audio, playerNumber) => {
              const playerSyncPoint = syncPoints.get(playerNumber);
              if (playerSyncPoint !== undefined) {
                const playerStartPosition = lastStartPositions.get(playerNumber);
                if (playerStartPosition !== undefined) {
                  const t = Math.max(0, Math.min(playerStartPosition, audio.duration || Infinity));
                  audio.currentTime = t;
                } else {
                  const startOffset = referenceStartPosition - referenceSyncPoint;
                  const t = Math.max(0, Math.min(playerSyncPoint + startOffset, audio.duration || Infinity));
                  audio.currentTime = t;
                }
              } else {
                const playerStartPosition = lastStartPositions.get(playerNumber);
                if (playerStartPosition !== undefined) {
                  const t = Math.max(0, Math.min(playerStartPosition, audio.duration || Infinity));
                  audio.currentTime = t;
                } else {
                  audio.currentTime = referenceStartPosition;
                }
              }
            });
          });
        }
        // Update last start positions if playing
        updateLastStartPositions();
        return;
      }
    }
    onRewindToLastStart();
  };

  const getJumpButtonLabel = () => {
    if (!hasSyncPoints) return "Set at least 1 sync point to jump";
    if (!hasJumpedToSyncPoints) return "Jump to Sync Points (J)";
    return "Clear Sync Points and Reset to Start (J)";
  };

  // Add keyboard handler for period key
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Skip hotkeys when user is editing text
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement ||
          (event.target instanceof HTMLElement && event.target.contentEditable === 'true')) {
        return;
      }
      
      if ((event.key === 'b' || event.key === 'B') && isBlindTestMode) {
        toggleBlindTestMode();
      }
      if (event.key === 'Escape' && isBlindTestMode) {
        toggleBlindTestMode();
      }
      if (event.key === '.' && isBlindTestMode && blindTestMode === 'basic') {
        toggleRandomSwitching();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isBlindTestMode, blindTestMode, toggleBlindTestMode, toggleRandomSwitching]);

  // Update handlePlayerSelect to use ordered switching when not random
  const handlePlayerSelect = (reverse: boolean = false, forceRandom: boolean = false) => {
    if (isBlindTestMode) {
      // Trigger spin animation for basic mode (button click)
      // For pick-fave and guess-track modes, the spin is triggered automatically in selectPlayer
      if (blindTestMode === 'basic') {
        triggerSpinAnimation();
      }
      
      // Get only included players for blind test mode
      const includedPlayers = Array.from(audioElements.keys())
        .filter(playerNum => blindTestIncludedPlayers.get(playerNum) ?? true)
        .sort(naturalSortNumbers);
      
      if (includedPlayers.length === 0) return; // No players included
      
      if (forceRandom || isRandomSwitching) {
        // If allowRepeats is false, exclude the current player
        const availablePlayers = blindTestAllowRepeats 
          ? includedPlayers 
          : includedPlayers.filter(num => num !== selectedPlayer);
        
        // If no available players (shouldn't happen if allowRepeats is true), fall back to all included
        const playersToChooseFrom = availablePlayers.length > 0 ? availablePlayers : includedPlayers;
        
        // Use simple random selection instead of getRandomPlayer when we need to exclude current player
        const newPlayer = blindTestAllowRepeats
          ? getRandomPlayer({ 
              lastPlayer: selectedPlayer, 
              consecutivePlays: 0,
              lastDoublePlayTime: Date.now(),
              truePlayerSwitches: 0,
              requireTrueSwitches: true
            })
          : playersToChooseFrom[Math.floor(Math.random() * playersToChooseFrom.length)];
        
        selectPlayer(newPlayer);
      } else {
        const currentIndex = includedPlayers.indexOf(selectedPlayer);
        let nextIndex;
        
        if (currentIndex === -1) {
          // Current player is not in included list, select first included player
          nextIndex = 0;
        } else {
          nextIndex = reverse 
            ? (currentIndex - 1 + includedPlayers.length) % includedPlayers.length
            : (currentIndex + 1) % includedPlayers.length;
        }
        
        const nextPlayer = includedPlayers[nextIndex];
        selectPlayer(nextPlayer);
      }

      // Start playback if not already playing
      if (!isPlaying) {
        const audio = audioElements.get(selectedPlayer);
        if (audio) {
          audio.play().then(() => {
            onPlayPause();
          });
        }
      }
      // Dispatch randomSwitch event
      window.dispatchEvent(new Event('randomSwitch'));
    } else {
      const validPlayers = Array.from(audioElements.keys()).sort(naturalSortNumbers);
      if (validPlayers.length === 0) return;
      
      if (forceRandom) {
        // Use random selection for non-blind-test mode
        const randomPlayer = validPlayers[Math.floor(Math.random() * validPlayers.length)];
        selectPlayer(randomPlayer);
      } else {
        const currentIndex = validPlayers.indexOf(selectedPlayer);
        const nextIndex = reverse 
          ? (currentIndex - 1 + validPlayers.length) % validPlayers.length
          : (currentIndex + 1) % validPlayers.length;
        const nextPlayer = validPlayers[nextIndex];
        
        selectPlayer(nextPlayer);
      }
    }
  };

  useEffect(() => {
    return setupKeyboardHandlers(
      {
        isPlaying,
        onPlayPause,
        onForward: seekForward,
        onRewind: seekRewind,
        onRewindToLastStart: rewindToLastStartSyncAware,
        onLoop: handleLoopClick,
        onBlindTest: toggleBlindTestMode,
        onSyncPoint: handleSyncPoint,
        onJumpToSyncPoints: handleSyncButton, // Use combined handler
        onClearSyncPoints: handleClearSyncPoints,
        onRemovePlayer: onRemove ? () => onRemove(Number(selectedPlayer)) : undefined,
        onToggleSelectedBlindTestIncluded: toggleBlindTestIncludedForSelected,
        onToggleAllBlindTestIncluded: toggleAllBlindTestIncluded,
        onToggleMono: toggleStereoMode, // Use new cycling function for keyboard shortcut
        onToggleDim: toggleMasterDim,
        onWaveformSync: handleSyncButton, // Use combined handler for consistency
        onCreateRegion,
        playerCount,
      },
      selectPlayer,
      audioElements,
      selectedPlayer,
      isBlindTestMode,
      handlePlayerSelect
    );
  }, [isPlaying, onPlayPause, onForward, onRewind, playerCount, selectPlayer, audioElements, selectedPlayer, isBlindTestMode, handleLoopClick, toggleBlindTestMode, handlePlayerSelect, handleSyncPoint, handleSyncButton, handleClearSyncPoints, onRemove, toggleBlindTestIncludedForSelected, toggleAllBlindTestIncluded, toggleStereoMode, toggleMasterDim]);

  const handleFastRewindStart = () => {
    hasLongPressed.current = false;
    longPressTimer.current = setTimeout(() => {
      hasLongPressed.current = true;
      // Play from start - reset to fresh state like first play
      audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
        audio.load();
      });
    }, 500);
  };

  const handleFastRewindEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      if (!hasLongPressed.current) {
        // Only trigger rewind if this is a touch event or if it's not a touch device
        if ('touches' in e || !isMobile) {
          seekRewind(10);
        }
      }
    }
  };

  const handleFastRewindClick = (e: React.MouseEvent) => {
    // Only handle click events on non-mobile devices
    if (!isMobile && !hasLongPressed.current) {
      seekRewind(10);
    }
  };

  const handleRewindStart = () => {
    hasLongPressed.current = false;
    longPressTimer.current = setTimeout(() => {
      hasLongPressed.current = true;
      // Return to last playback position
      if (onRewindToLastStart) {
        onRewindToLastStart();
      }
    }, 500);
  };

  const handleRewindEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      if (!hasLongPressed.current) {
        // Only trigger rewind if this is a touch event or if it's not a touch device
        if ('touches' in e || !isMobile) {
          seekRewind(3);
        }
      }
    }
  };

  const handleRewindClick = (e: React.MouseEvent) => {
    // Only handle click events on non-mobile devices
    if (!isMobile && !hasLongPressed.current) {
      seekRewind(3);
    }
  };

  const getLoopButtonStyles = () => {
    if (isLooping) {
      return {
        color: "accent.400",
      };
    }
    if (loopState === 'setting_end') {
      return {
        color: "#F56565", // red.500
        animation: `${pulse} 1s ease-in-out infinite`,
      };
    }
    if (!isPlaying && !isLooping) {
      return {
        color: "gray.400",
        opacity: 0.5,
        cursor: "not-allowed",
      };
    }
    return {
      color: "gray.400",
    };
  };

  const getLoopTooltip = () => {
    if (!isPlaying) return "Start playback to set loop points";
    if (isLooping) return "Disable Loop (L)";
    if (loopState === 'setting_end') return "Set Loop End Point (L)";
    return "Set Loop Start Point (L)";
  };

  return (
    <Box position="relative">
      <Center data-testid="transport-controls">
        <VStack spacing={6}>
            <Box position="relative" width="200px">
            <Box position="absolute" right="-60px" top="50%" transform="translateY(-50%)">
              <Tooltip 
                label={
                  <VStack spacing={1} color="gray.800" align="start">
                    <Text>Set Loop Points</Text>
                    <HStack spacing={2}>
                      <TooltipKeyBox>L</TooltipKeyBox>
                      <Text>or</Text>
                      <TooltipKeyBox>⌥ drag</TooltipKeyBox>
                    </HStack>
                  </VStack>
                } 
                placement="top"
                hasArrow
              >
                <IconButton
                  data-testid="loop-button"
                  aria-label="Loop Control"
                  icon={<TbRepeat size={36} />}
                  onClick={handleLoopClick}
                  size="lg"
                  width="72px"
                  height="72px"
                  variant="ghost"
                  {...getLoopButtonStyles()}
                  _hover={{ 
                    color: isLooping ? "accent.400" : "gray.400",
                    transform: 'scale(1.05)',
                    borderColor: 'transparent'
                  }}
                  _active={{ 
                    color: isLooping ? "accent.500" : "gray.500",
                    transform: 'scale(0.95)',
                    borderColor: 'transparent'
                  }}
                  _focus={{ 
                    boxShadow: "none",
                    outline: "none",
                  }}
                  _focusVisible={{ 
                    boxShadow: "none",
                    outline: "none",
                  }}
                  sx={{
                    '&:focus': {
                      outline: 'none',
                      boxShadow: 'none',
                      background: 'transparent',
                    },
                    '&:focus-visible': {
                      outline: 'none',
                      boxShadow: 'none',
                      background: 'transparent',
                    },
                  }}
                  transition="all 0.2s ease-in-out"
                />
              </Tooltip>
            </Box>

            {/* Add Sync Point Controls */}
            <Box position="absolute" left="25px" top="-20px" transform="translateX(-50%)" zIndex={2}>
              <Tooltip 
                label={
                  <HStack spacing={2} color="gray.800">
                    <Text>Set Anchor Point</Text>
                    <TooltipKeyBox>A</TooltipKeyBox>
                  </HStack>
                } 
                placement="top"
                hasArrow
              >
                <IconButton
                  data-testid="sync-point-button"
                  aria-label="Set Anchor Point"
                  icon={
                    <Box position="relative">
                      <FaMapMarker size={24} />
                      <Text
                        position="absolute"
                        top="42%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        fontSize="14px"
                        fontWeight="bold"
                        color="black"
                      >
                        A
                      </Text>
                    </Box>
                  }
                  onClick={handleSyncPoint}
                  size="lg"
                  width="48px"
                  height="48px"
                  variant="ghost"
                  color={hasSyncPoints ? "accent.400" : "gray.400"}
                  isDisabled={hasJumpedToSyncPoints}
                  _hover={{ 
                    color: hasSyncPoints ? "accent.400" : "gray.400",
                    transform: 'scale(1.05)',
                  }}
                  _active={{ 
                    color: hasSyncPoints ? "accent.500" : "gray.400",
                    transform: 'scale(0.95)',
                  }}
                />
              </Tooltip>
            </Box>
            <Box position="absolute" right="25px" top="-20px" transform="translateX(50%)" zIndex={2}>
              <Tooltip 
                label={
                  isSynchronizing ? (
                    <HStack spacing={2} color="gray.800">
                      <Text>Synchronizing waveforms...</Text>
                    </HStack>
                  ) : hasSyncPoints ? (
                    <HStack spacing={2} color="gray.800">
                      <Text>Jump to Sync Points</Text>
                      <TooltipKeyBox>S</TooltipKeyBox>
                    </HStack>
                  ) : (
                    <HStack spacing={2} color="gray.800">
                      <Text>Auto-sync Waveforms</Text>
                      <TooltipKeyBox>S</TooltipKeyBox>
                    </HStack>
                  )
                } 
                placement="top"
                hasArrow
              >
                <IconButton
                  data-testid="jump-sync-button"
                  aria-label={isSynchronizing ? "Synchronizing..." : hasSyncPoints ? (hasJumpedToSyncPoints ? "Clear Sync Points" : "Jump to Sync Points") : "Auto-sync waveforms"}
                  icon={
                    <Box position="relative">
                      <FaMapMarker size={24} />
                      <Text
                        position="absolute"
                        top="42%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        fontSize="14px"
                        fontWeight="bold"
                        color="black"
                      >
                        S
                      </Text>
                    </Box>
                  }
                  onClick={handleSyncButton}
                  size="lg"
                  width="48px"
                  height="48px"
                  variant="ghost"
                  color={isSynchronizing ? "accent.500" : hasJumpedToSyncPoints ? "accent.400" : "gray.400"}
                  isDisabled={audioElements.size < 2 || isSynchronizing}
                  animation={isSynchronizing ? `${pulseSync} 1s ease-in-out infinite` : (hasSyncPoints && syncPoints.size >= 2 && !hasJumpedToSyncPoints ? `${pulseSync} 1s ease-in-out infinite` : undefined)}
                  _hover={{ 
                    color: isSynchronizing ? "accent.500" : hasJumpedToSyncPoints ? "accent.400" : "accent.400",
                    transform: 'scale(1.05)',
                  }}
                  _active={{ 
                    color: isSynchronizing ? "accent.600" : hasJumpedToSyncPoints ? "accent.500" : "accent.500",
                    transform: 'scale(0.95)',
                  }}
                />
              </Tooltip>
            </Box>

            {/* Filter button overlay - below anchor button, same vertical height as share button */}
            {onFilterModalOpen && (
              <Box position="absolute" left="25px" top="calc(100% + 3px)" transform="translateX(-50%) translateY(-50%)" zIndex={10}>
                <FilterControlButton
                  filterType={filterType}
                  filterFrequency={filterFrequency}
                  onClick={onFilterModalOpen}
                  onToggle={onToggleFilter}
                  hasFilterBeenSet={hasFilterBeenSet}
                />
              </Box>
            )}

            {/* DIM button overlay - 35px above mono/sides button */}
            <Box position="absolute" right="25px" top="calc(100% - 32px)" transform="translateX(50%) translateY(-50%)" zIndex={10}>
              <Tooltip 
                label={
                  <HStack spacing={2} color="gray.800">
                    <Text>Dim Master Volume</Text>
                    <TooltipKeyBox>D</TooltipKeyBox>
                  </HStack>
                } 
                placement="bottom"
                hasArrow
              >
                <IconButton
                  data-testid="dim-button"
                  aria-label={`Dim Master Volume (${isDimmed ? 'Dimmed' : 'Normal'})`}
                  icon={<FaVolumeDown size={20} />}
                  onClick={toggleMasterDim}
                  size="md"
                  borderRadius="full"
                  variant="ghost"
                  bg="transparent"
                  color={isDimmed ? "red.600" : "gray.400"}
                  animation={isDimmed ? `${pulseRed} 1.5s ease-in-out infinite` : undefined}
                  _hover={{
                    color: isDimmed ? "red.500" : "accent.400",
                    transform: 'scale(1.05)',
                    borderColor: 'transparent'
                  }}
                  _active={{
                    color: isDimmed ? "red.700" : "accent.500",
                    transform: 'scale(0.95)',
                    borderColor: 'transparent'
                  }}
                  _focus={{ 
                    boxShadow: "none",
                    outline: "none",
                  }}
                  _focusVisible={{ 
                    boxShadow: "none",
                    outline: "none",
                  }}
                />
              </Tooltip>
            </Box>

            {/* Mono/Sides button overlay - below sync button, same vertical height as share button */}
            <Box position="absolute" right="25px" top="calc(100% + 3px)" transform="translateX(50%) translateY(-50%)" zIndex={10}>
              <Tooltip 
                label={
                  <HStack spacing={2} color="gray.800">
                    <Text>Stereo/Mono/Sides</Text>
                    <TooltipKeyBox>M</TooltipKeyBox>
                  </HStack>
                } 
                placement="bottom"
                hasArrow
              >
                <IconButton
                  data-testid="stereo-mode-button"
                  aria-label={`Cycle Stereo Mode (Current: ${stereoMode})`}
                  icon={<StereoModeIcon mode={stereoMode} size={24} />}
                  onClick={toggleStereoMode}
                  size="lg"
                  borderRadius="full"
                  variant="ghost"
                  bg="transparent"
                  color={stereoMode !== 'stereo' ? "red.600" : "gray.400"}
                  animation={stereoMode !== 'stereo' ? `${pulseRed} 1.5s ease-in-out infinite` : undefined}
                  _hover={{
                    color: stereoMode !== 'stereo' ? "red.500" : "accent.400",
                    transform: 'scale(1.05)',
                    borderColor: 'transparent'
                  }}
                  _active={{
                    color: stereoMode !== 'stereo' ? "red.700" : "accent.500",
                    transform: 'scale(0.95)',
                    borderColor: 'transparent'
                  }}
                  _focus={{ 
                    boxShadow: "none",
                    outline: "none",
                  }}
                  _focusVisible={{ 
                    boxShadow: "none",
                    outline: "none",
                  }}
                />
              </Tooltip>
            </Box>

            {!isMobile && (
              <>
                <Box position="absolute" right="-140px" top="calc(100% + 3px)" transform="translateY(-50%)">
                  <ShareButton loadedFiles={loadedFiles} />
                </Box>
                {shareData ? (
                  <>
                    <Box position="absolute" left="-140px" top="calc(100% + 3px)" transform="translateY(-50%)">
                      <ReloadButton />
                    </Box>
                    {shareData.allowDownloads && (
                      <Box position="absolute" left="-220px" top="calc(100% + 3px)" transform="translateY(-50%)">
                        <DownloadButton
                          originalUrls={shareData.originalUrls}
                          isOwner={shareData.isOwner}
                          allowDownloads={shareData.allowDownloads}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  onFilesSelected && (
                    <Box position="absolute" left="-140px" top="calc(100% + 3px)" transform="translateY(-50%)">
                      <AddFilesButton
                        onFilesSelected={onFilesSelected}
                        loadedFiles={loadedFiles}
                        onLoadingChange={onAddFilesLoadingChange}
                        onProgressChange={onAddFilesProgressChange}
                      />
                    </Box>
                  )
                )}
              </>
            )}
            <Box position="relative" width="200px">
              <Box position="absolute" left="-60px" top="50%" transform="translateY(-50%)">
                <Tooltip label={<>Toggle Blind Test Mode <TooltipKeyBox>B</TooltipKeyBox></>} placement="top" hasArrow>
                  <IconButton
                    data-testid="blind-test-button"
                    aria-label="Toggle Blind Test Mode"
                    icon={<FaEyeSlash size={36} />}
                    onClick={toggleBlindTestMode}
                    size="lg"
                    width="72px"
                    height="72px"
                    variant="ghost"
                    color={isBlindTestMode ? "accent.400" : "gray.400"}
                    _hover={{ 
                      color: isBlindTestMode ? "accent.400" : "gray.400",
                      transform: 'scale(1.05)',
                      borderColor: 'transparent'
                    }}
                    _active={{ 
                      color: isBlindTestMode ? "accent.500" : "gray.400",
                      transform: 'scale(0.95)',
                      borderColor: 'transparent'
                    }}
                    _focus={{ boxShadow: "none" }}
                    _focusVisible={{ boxShadow: "none" }}
                  />
                </Tooltip>
              </Box>
              <Center>
                <VStack spacing={2}>
                  <Tooltip 
                    label={
                      <HStack spacing={2} color="gray.800">
                        <Text>Cycle audio players</Text>
                        <TooltipKeyBox>/</TooltipKeyBox>
                      </HStack>
                    } 
                    placement="top"
                    hasArrow
                  >
                    <Button
                      data-testid="player-switch-button"
                      width="72px"
                      height="72px"
                      borderRadius="full"
                      fontSize="3xl"
                      fontWeight="bold"
                      onClick={() => handlePlayerSelect(false)}
                      isDisabled={playerCount < 2 || (isBlindTestMode && (blindTestMode === 'pick-fave' || blindTestMode === 'guess-track'))}
                      bg="transparent"
                      color="accent.400"
                      border="2px solid"
                      borderColor="accent.500"
                      position="relative"
                      outline="none"
                      animation={isSpinning ? `${spin} 0.5s ease-in-out` : undefined}
                      sx={{
                        textShadow: '0 0 10px var(--chakra-colors-accent-400)',
                        '&:focus': {
                          background: 'transparent',
                        },
                        '&:focus-visible': {
                          outline: 'none',
                          boxShadow: 'none',
                          background: 'transparent',
                        },
                        touchAction: 'manipulation',
                        '& svg': {
                          filter: 'drop-shadow(0 0 10px var(--chakra-colors-accent-400))',
                        }
                      }}
                      _focusVisible={{
                        outline: 'none',
                        boxShadow: 'none',
                        bg: 'transparent',
                      }}
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: '-1px',
                        right: '-1px',
                        bottom: '-1px',
                        left: '-1px',
                        borderRadius: 'full',
                        border: '2px solid transparent',
                        background: 'linear-gradient(45deg, accent.400, accent.600)',
                        WebkitMask: 
                          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                      }}
                      _hover={{
                        transform: "scale(1.05)",
                        bg: 'transparent',
                        color: "accent.300",
                        borderColor: "accent.400",
                        _before: {
                          background: 'linear-gradient(45deg, accent.300, accent.500)',
                        },
                        textShadow: '0 0 15px var(--chakra-colors-accent-300)',
                        '& svg': {
                          filter: 'drop-shadow(0 0 15px var(--chakra-colors-accent-300))',
                        }
                      }}
                      _active={{
                        transform: "scale(0.95)",
                        bg: 'transparent',
                        color: "accent.500",
                        borderColor: "accent.600",
                        _before: {
                          background: 'linear-gradient(45deg, accent.500, accent.700)',
                        },
                        textShadow: '0 0 10px var(--chakra-colors-accent-500)',
                        '& svg': {
                          filter: 'drop-shadow(0 0 10px var(--chakra-colors-accent-500))',
                        }
                      }}
                    >
                      {isBlindTestMode ? (isRandomSwitching ? <Text fontSize="40px" lineHeight="1" display="inline-flex" alignItems="center" justifyContent="center" width="32px" height="32px">?</Text> : <FaArrowDown size={32} />) : selectedPlayer}
                    </Button>
                  </Tooltip>
                  
                  {isBlindTestMode && blindTestMode === 'basic' && (
                    <Tooltip 
                      label={
                        <HStack spacing={2} color="gray.800">
                          <Text>Random/sequential player switching</Text>
                          {!isMobile && <TooltipKeyBox>.</TooltipKeyBox>}
                        </HStack>
                      } 
                      placement="bottom"
                      hasArrow
                      isOpen={isMobile ? showSwitchTooltip : undefined}
                    >
                      <Box>
                        <Switch
                          colorScheme="accent"
                          size="md"
                          isChecked={isRandomSwitching}
                          onChange={(e) => {
                            toggleRandomSwitching();
                            // Blur the switch to lose focus after clicking
                            e.target.blur();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                          sx={{
                            'span.chakra-switch__track': {
                              backgroundColor: 'transparent',
                              border: '2px solid',
                              borderColor: isRandomSwitching ? 'accent.400' : 'gray.600',
                            },
                            'span.chakra-switch__thumb': {
                              backgroundColor: isRandomSwitching ? 'accent.400' : 'gray.400',
                            },
                            _hover: {
                              'span.chakra-switch__track': {
                                borderColor: isRandomSwitching ? 'accent.300' : 'gray.500',
                              },
                              'span.chakra-switch__thumb': {
                                backgroundColor: isRandomSwitching ? 'accent.300' : 'gray.300',
                              },
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </VStack>
              </Center>
            </Box>
          </Box>

          <HStack spacing={4} justify="center">
            <Tooltip 
              label={
                <VStack spacing={2} align="start" color="gray.800" minW="300px">
                  <HStack spacing={2}>
                    <Text>Rewind 10 seconds</Text>
                    <TooltipKeyBox>⇧←</TooltipKeyBox>
                  </HStack>
                  <HStack spacing={2}>
                    <Text>Return to the start</Text>
                    <TooltipKeyBox>⌘←</TooltipKeyBox>
                  </HStack>
                </VStack>
              } 
              placement="top"
              hasArrow
            >
              <IconButton
                data-testid="rewind-10-button"
                aria-label="Rewind 10 seconds"
                icon={<FaFastBackward />}
                onClick={handleFastRewindClick}
                onMouseDown={handleFastRewindStart}
                onMouseUp={handleFastRewindEnd}
                onMouseLeave={handleFastRewindEnd}
                onTouchStart={handleFastRewindStart}
                onTouchEnd={handleFastRewindEnd}
                variant="ghost"
                size="lg"
                color="gray.400"
                _hover={{ 
                  color: "accent.400",
                  transform: 'scale(1.05)',
                  borderColor: 'transparent'
                }}
                _active={{ 
                  color: "accent.500",
                  transform: 'scale(0.95)',
                  borderColor: 'transparent'
                }}
              />
            </Tooltip>
            <Tooltip 
              label={
                <VStack spacing={2} align="start" color="gray.800" minW="300px">
                  <HStack spacing={2}>
                    <Text>Rewind 3 seconds</Text>
                    <TooltipKeyBox>←</TooltipKeyBox>
                  </HStack>
                  <HStack spacing={2}>
                    <Text>Replay from last position</Text>
                    <TooltipKeyBox>⌥←</TooltipKeyBox>
                  </HStack>
                  {playerCount === 2 && (
                    <HStack spacing={2}>
                      <Text>Replay and switch player</Text>
                      <TooltipKeyBox>⌃⌥←</TooltipKeyBox>
                    </HStack>
                  )}
                  {isMobile && (
                    <Text fontSize="sm" color="gray.600" fontStyle="italic">
                      Hold for return to last position
                    </Text>
                  )}
                </VStack>
              } 
              placement="top"
              hasArrow
            >
              <IconButton
                data-testid="rewind-3-button"
                aria-label="Rewind 3 seconds"
                icon={<FaStepBackward />}
                onClick={handleRewindClick}
                onMouseDown={handleRewindStart}
                onMouseUp={handleRewindEnd}
                onMouseLeave={handleRewindEnd}
                onTouchStart={handleRewindStart}
                onTouchEnd={handleRewindEnd}
                variant="ghost"
                size="lg"
                color="gray.400"
                _hover={{ 
                  color: "accent.400",
                  transform: 'scale(1.05)',
                  borderColor: 'transparent'
                }}
                _active={{ 
                  color: "accent.500",
                  transform: 'scale(0.95)',
                  borderColor: 'transparent'
                }}
              />
            </Tooltip>

            <Tooltip 
              label={
                <HStack spacing={2} color="gray.800">
                  <Text>Play/Pause</Text>
                  <TooltipKeyBox>Space</TooltipKeyBox>
                </HStack>
              } 
              placement="top"
              hasArrow
            >
              <IconButton
                data-testid="play-pause-button"
                aria-label="Play/Pause"
                icon={isPlaying ? <FaPause /> : <FaPlay />}
                onClick={() => {
                  if (isPlaying) {
                    pauseAll();
                  } else {
                    onPlayPause();
                  }
                }}
                size="lg"
                variant="ghost"
                color="gray.400"
                _hover={{ 
                  color: "accent.400",
                  transform: 'scale(1.05)',
                  borderColor: 'transparent'
                }}
                _active={{ 
                  color: "accent.500",
                  transform: 'scale(0.95)',
                  borderColor: 'transparent'
                }}
              />
            </Tooltip>
            <Tooltip 
              label={
                <HStack spacing={2} color="gray.800">
                  <Text>Forward 3 seconds</Text>
                  <TooltipKeyBox>→</TooltipKeyBox>
                </HStack>
              } 
              placement="top"
              hasArrow
            >
              <IconButton
                data-testid="forward-3-button"
                aria-label="Forward 3 seconds"
                icon={<FaStepForward />}
                onClick={() => seekForward(3)}
                variant="ghost"
                size="lg"
                color="gray.400"
                _hover={{ 
                  color: "accent.400",
                  transform: 'scale(1.05)',
                  borderColor: 'transparent'
                }}
                _active={{ 
                  color: "accent.500",
                  transform: 'scale(0.95)',
                  borderColor: 'transparent'
                }}
              />
            </Tooltip>
            <Tooltip 
              label={
                <HStack spacing={2} color="gray.800">
                  <Text>Forward 10 seconds</Text>
                  <TooltipKeyBox>⇧→</TooltipKeyBox>
                </HStack>
              } 
              placement="top"
              hasArrow
            >
              <IconButton
                data-testid="forward-10-button"
                aria-label="Forward 10 seconds"
                icon={<FaFastForward />}
                onClick={() => seekForward(10)}
                variant="ghost"
                size="lg"
                color="gray.400"
                _hover={{ 
                  color: "accent.400",
                  transform: 'scale(1.05)',
                  borderColor: 'transparent'
                }}
                _active={{ 
                  color: "accent.500",
                  transform: 'scale(0.95)',
                  borderColor: 'transparent'
                }}
              />
            </Tooltip>
          </HStack>
        </VStack>
      </Center>
    </Box>
  );
};

export default TransportControls; 